/**
 * SettingsCard — shared section shell for settings tabs.
 *
 * Each settings tab is composed of one or more SettingsCards. The card
 * renders: title, optional hint, body, and a sticky footer action bar
 * (tablet-first: buttons pinned to the right, 64px min-height).
 */
import { type FormEventHandler, type ReactNode } from 'react';
import { Card, CardBody, CardFooter, CardHeader } from '../../../shared/ui/Card';
import { cn } from '../../../shared/utils/cn';

export function SettingsCard({
  title,
  hint,
  children,
  footer,
  onSubmit,
  className,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
  footer?: ReactNode;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  className?: string;
}): JSX.Element {
  const body = (
    <>
      <CardHeader>
        <div className="min-w-0">
          <h2 className="text-biz-lg biz-heading font-semibold">{title}</h2>
          {hint ? (
            <p className="text-biz-sm text-biz-muted-foreground mt-biz-content">
              {hint}
            </p>
          ) : null}
        </div>
      </CardHeader>
      <CardBody className="space-y-biz-content-lg">{children}</CardBody>
      {footer ? <CardFooter>{footer}</CardFooter> : null}
    </>
  );

  return (
    <Card className={cn('mb-biz-section', className)}>
      {onSubmit ? (
        <form onSubmit={onSubmit} noValidate>
          {body}
        </form>
      ) : (
        body
      )}
    </Card>
  );
}
